import { type FormEvent, useState } from 'react';

import { getClubsApi } from '@/lib/api/clubsApi';
import { createOrganizerTeamApi } from '@/lib/api/teamsApi';
import { FederalClassification } from '@shared/enums/domain.enum';
import type { Competition, CreateTeamPayload, Team } from '@shared/types/entities';
import { useMutation, useQuery } from '@tanstack/react-query';
import { UserPlus, X } from 'lucide-react';
import { toast } from 'sonner';

type PlayerInput = { firstName: string; lastName: string; licenseNumber: string };

interface Props {
  competition: Competition;
  organizationId: number;
  onClose: () => void;
  onCreated: (team: Team) => void;
}

const EMPTY_PLAYER = (): PlayerInput => ({ firstName: '', lastName: '', licenseNumber: '' });
const INPUT_CLASS =
  'w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none';

/**
 * An org administrator registers other players with their OWN account.
 * The sporting captain is TeamPlayer #1, not the Stripe payer / account owner.
 * Each team retains its own checkout, status and confirmation webhook.
 */
export function OrganizerTeamRegistrationModal({ competition, organizationId, onClose, onCreated }: Props) {
  const playerCount = competition.playersPerTeam ?? 0;
  const [name, setName] = useState('');
  const [clubId, setClubId] = useState('');
  const [captain, setCaptain] = useState<PlayerInput>(EMPTY_PLAYER);
  const [captainPhone, setCaptainPhone] = useState('');
  const [captainBirthDate, setCaptainBirthDate] = useState('');
  const [captainFederalClassification, setCaptainFederalClassification] = useState('');
  const [players, setPlayers] = useState<PlayerInput[]>(() =>
    Array.from({ length: Math.max(0, playerCount - 1) }, EMPTY_PLAYER),
  );
  const [licensesDeclared, setLicensesDeclared] = useState(false);
  const [acceptedCgu, setAcceptedCgu] = useState(false);
  const [acknowledgedNoWithdrawal, setAcknowledgedNoWithdrawal] = useState(false);

  // This endpoint returns only clubs affiliated with the selected association.
  const { data: clubsResult } = useQuery({
    queryKey: ['gv02-affiliated-clubs', organizationId],
    queryFn: () => getClubsApi({ organizationId, limit: 100 }),
  });
  const clubs = clubsResult?.data ?? [];
  const requiresLicense = competition.restrictedToMembers;
  const requiresAge = competition.allowedAgeCategories.length > 0;
  const requiresClassification = competition.allowedClassifications.length > 0;
  const allPlayers = [captain, ...players];
  const canSubmit =
    playerCount >= 1 &&
    (!competition.requireTeamName || name.trim().length > 0) &&
    (!competition.restrictedAffiliates || !!clubId) &&
    allPlayers.every(p => p.firstName.trim() && p.lastName.trim() && (!requiresLicense || p.licenseNumber.trim())) &&
    (!requiresLicense || licensesDeclared) &&
    (!requiresAge || !!captainBirthDate) &&
    (!requiresClassification || !!captainFederalClassification) &&
    acceptedCgu &&
    acknowledgedNoWithdrawal;

  const createMutation = useMutation({
    mutationFn: (payload: CreateTeamPayload) => createOrganizerTeamApi(payload),
    onSuccess: team => {
      toast.success(`Équipe « ${team.name} » enregistrée`);
      onCreated(team);
    },
    onError: (error: unknown) => {
      toast.error(error instanceof Error ? error.message : "Impossible d'inscrire cette équipe");
    },
  });

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit || createMutation.isPending) return;
    createMutation.mutate({
      competitionId: competition.id,
      ...(name.trim() && { name: name.trim() }),
      ...(clubId && { clubId: Number(clubId) }),
      ...(captainPhone.trim() && { captainPhone: captainPhone.trim() }),
      captainFirstName: captain.firstName.trim(),
      captainLastName: captain.lastName.trim(),
      ...(captain.licenseNumber.trim() && { captainLicenseNumber: captain.licenseNumber.trim() }),
      ...(requiresAge && { captainBirthDate }),
      ...(requiresClassification && {
        captainFederalClassification: captainFederalClassification as FederalClassification,
      }),
      captainLicenseAutoDeclared: requiresLicense ? licensesDeclared : false,
      players: players.map(p => ({
        firstName: p.firstName.trim(),
        lastName: p.lastName.trim(),
        licenseNumber: p.licenseNumber.trim(),
        licenseAutoDeclared: requiresLicense ? licensesDeclared : false,
      })),
      acceptedCgu,
      acknowledgedNoWithdrawal,
    });
  }

  function updateOther(index: number, field: keyof PlayerInput, value: string) {
    setPlayers(prev => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  }

  const playerInputs = (value: PlayerInput, onChange: (field: keyof PlayerInput, value: string) => void) => (
    <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
      <label className='text-xs font-medium text-slate-700'>
        Prénom
        <input
          required
          maxLength={50}
          value={value.firstName}
          onChange={e => onChange('firstName', e.target.value)}
          className={INPUT_CLASS}
        />
      </label>
      <label className='text-xs font-medium text-slate-700'>
        Nom
        <input
          required
          maxLength={50}
          value={value.lastName}
          onChange={e => onChange('lastName', e.target.value)}
          className={INPUT_CLASS}
        />
      </label>
      <label className='text-xs font-medium text-slate-700'>
        Licence {requiresLicense ? '(obligatoire)' : '(facultatif)'}
        <input
          required={requiresLicense}
          maxLength={30}
          value={value.licenseNumber}
          onChange={e => onChange('licenseNumber', e.target.value)}
          className={INPUT_CLASS}
        />
      </label>
    </div>
  );

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4'
      role='presentation'
      onClick={onClose}
    >
      <div
        className='w-full max-w-2xl rounded-2xl bg-white shadow-xl'
        role='dialog'
        aria-modal='true'
        aria-label='Inscrire une équipe pour des joueurs'
        onClick={e => e.stopPropagation()}
      >
        <div className='flex items-center justify-between border-b border-slate-100 px-6 py-4'>
          <h2 className='flex items-center gap-2 text-lg font-bold text-slate-900'>
            <UserPlus className='h-5 w-5 text-blue-600' /> Inscrire une équipe
          </h2>
          <button
            type='button'
            onClick={onClose}
            aria-label='Fermer'
            className='rounded-lg p-1.5 text-slate-400 hover:bg-slate-100'
          >
            <X className='h-5 w-5' />
          </button>
        </div>
        <form onSubmit={submit}>
          <div className='max-h-[65vh] space-y-5 overflow-y-auto px-6 py-5'>
            <p className='text-sm text-slate-600'>
              Vous inscrivez des joueurs avec votre compte administrateur. Vous avancerez le paiement avec votre propre
              moyen de paiement ; l'association organisatrice encaissera via Stripe Connect. Le capitaine sportif n'est
              pas le payeur.
              {Number(competition.price ?? 0) > 0 && ' Le règlement se fait séparément pour chaque équipe.'}
            </p>
            <label className='block text-sm font-semibold text-slate-700'>
              Nom de l'équipe {competition.requireTeamName ? '*' : '(facultatif)'}
              <input
                required={competition.requireTeamName}
                maxLength={255}
                value={name}
                onChange={e => setName(e.target.value)}
                className={INPUT_CLASS}
              />
            </label>
            <label className='block text-sm font-semibold text-slate-700'>
              Club {competition.restrictedAffiliates ? '*' : '(facultatif)'}
              <select
                required={competition.restrictedAffiliates}
                value={clubId}
                onChange={e => setClubId(e.target.value)}
                className={INPUT_CLASS}
              >
                <option value=''>Sans club</option>
                {clubs.map(club => (
                  <option key={club.id} value={club.id}>
                    {club.name}
                  </option>
                ))}
              </select>
              {competition.restrictedAffiliates && clubs.length === 0 && (
                <span className='text-xs text-amber-700'>
                  Aucun club affilié disponible : inscription impossible pour ce concours.
                </span>
              )}
            </label>
            <section className='space-y-3 rounded-xl border border-slate-200 p-4'>
              <h3 className='text-sm font-semibold text-slate-800'>Joueur 1 — capitaine sportif</h3>
              {playerInputs(captain, (field, value) => setCaptain(prev => ({ ...prev, [field]: value })))}
              <label className='block text-xs font-medium text-slate-700'>
                Téléphone du capitaine (facultatif)
                <input
                  type='tel'
                  maxLength={15}
                  value={captainPhone}
                  onChange={e => setCaptainPhone(e.target.value)}
                  className={INPUT_CLASS}
                />
              </label>
              {requiresAge && (
                <label className='block text-xs font-medium text-slate-700'>
                  Date de naissance du capitaine *
                  <input
                    type='date'
                    required
                    value={captainBirthDate}
                    onChange={e => setCaptainBirthDate(e.target.value)}
                    className={INPUT_CLASS}
                  />
                </label>
              )}
              {requiresClassification && (
                <label className='block text-xs font-medium text-slate-700'>
                  Classification du capitaine *
                  <select
                    required
                    value={captainFederalClassification}
                    onChange={e => setCaptainFederalClassification(e.target.value)}
                    className={INPUT_CLASS}
                  >
                    <option value=''>Choisir</option>
                    {competition.allowedClassifications.map(value => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </section>
            {players.map((player, index) => (
              <section key={index} className='space-y-3 rounded-xl border border-slate-200 p-4'>
                <h3 className='text-sm font-semibold text-slate-800'>Joueur {index + 2}</h3>
                {playerInputs(player, (field, value) => updateOther(index, field, value))}
              </section>
            ))}
            {requiresLicense && (
              <label className='flex items-start gap-2 text-sm text-slate-700'>
                <input
                  type='checkbox'
                  checked={licensesDeclared}
                  onChange={e => setLicensesDeclared(e.target.checked)}
                  className='mt-1'
                />
                Je confirme sur l'honneur les déclarations de licence des {playerCount} joueurs. Aucune vérification
                fédérale automatique n'est effectuée.
              </label>
            )}
            <label className='flex items-start gap-2 text-sm text-slate-700'>
              <input
                type='checkbox'
                checked={acceptedCgu}
                onChange={e => setAcceptedCgu(e.target.checked)}
                className='mt-1'
              />
              J'accepte les conditions générales pour cette inscription.
            </label>
            <label className='flex items-start gap-2 text-sm text-slate-700'>
              <input
                type='checkbox'
                checked={acknowledgedNoWithdrawal}
                onChange={e => setAcknowledgedNoWithdrawal(e.target.checked)}
                className='mt-1'
              />
              Je reconnais séparément l'absence de droit de rétractation pour cette inscription à une activité de
              loisirs à date déterminée.
            </label>
          </div>
          <div className='flex items-center justify-between gap-3 border-t border-slate-100 px-6 py-4'>
            <p className='text-xs text-slate-500'>
              {playerCount} joueurs · {((competition.price ?? 0) / 100).toFixed(2)} € / équipe
            </p>
            <div className='flex gap-2'>
              <button type='button' onClick={onClose} className='rounded-lg border border-slate-200 px-4 py-2 text-sm'>
                Annuler
              </button>
              <button
                type='submit'
                disabled={!canSubmit || createMutation.isPending}
                className='rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50'
              >
                {createMutation.isPending ? 'Enregistrement…' : "Enregistrer l'équipe"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
